/**
 * User Data Cache - Prevents repeated DB calls for user profile/roles
 * 
 * This dramatically reduces load on Supabase when many users are online:
 * - Caches user roles, profile, and model data
 * - 5-minute TTL with instant access
 * - Invalidates on explicit user actions (not on every page load)
 */

interface UserRolesCache {
  roles: string[];
  isAdmin: boolean;
  isContributor: boolean;
  timestamp: number;
}

interface UserProfileCache {
  display_name?: string;
  profile_picture_url?: string;
  bio?: string;
  timestamp: number;
}

interface CacheStore {
  roles: Map<string, UserRolesCache>;
  profiles: Map<string, UserProfileCache>;
}

// In-memory cache (persists across component remounts)
const cache: CacheStore = {
  roles: new Map(),
  profiles: new Map(),
};

// Cache TTL: 5 minutes
const CACHE_TTL_MS = 5 * 60 * 1000;

// LocalStorage keys for persistence across page refreshes
const LS_ROLES_KEY = 'user_roles_cache';
const LS_PROFILE_KEY = 'user_profile_cache';

/**
 * Initialize cache from localStorage on module load
 */
function initFromStorage() {
  try {
    const rolesStr = localStorage.getItem(LS_ROLES_KEY);
    if (rolesStr) {
      const parsed = JSON.parse(rolesStr) as Record<string, UserRolesCache>;
      const now = Date.now();
      Object.entries(parsed).forEach(([userId, data]) => {
        if (now - data.timestamp < CACHE_TTL_MS) {
          cache.roles.set(userId, data);
        }
      });
    }

    const profileStr = localStorage.getItem(LS_PROFILE_KEY);
    if (profileStr) {
      const parsed = JSON.parse(profileStr) as Record<string, UserProfileCache>;
      const now = Date.now();
      Object.entries(parsed).forEach(([userId, data]) => {
        if (now - data.timestamp < CACHE_TTL_MS) {
          cache.profiles.set(userId, data);
        }
      });
    }
  } catch {
    // Silently fail - cache will be empty
  }
}

// Initialize on module load
initFromStorage();

/**
 * Persist cache to localStorage
 */
function persistToStorage() {
  try {
    const rolesObj: Record<string, UserRolesCache> = {};
    cache.roles.forEach((v, k) => { rolesObj[k] = v; });
    localStorage.setItem(LS_ROLES_KEY, JSON.stringify(rolesObj));

    const profileObj: Record<string, UserProfileCache> = {};
    cache.profiles.forEach((v, k) => { profileObj[k] = v; });
    localStorage.setItem(LS_PROFILE_KEY, JSON.stringify(profileObj));
  } catch {
    // Silently fail - localStorage might be full
  }
}

/**
 * Get cached user roles (returns null if not cached or expired)
 */
export function getCachedRoles(userId: string): UserRolesCache | null {
  const cached = cache.roles.get(userId);
  if (!cached) return null;
  
  if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
    cache.roles.delete(userId);
    return null;
  }
  
  return cached;
}

/**
 * Set user roles in cache
 */
export function setCachedRoles(userId: string, roles: string[]): void {
  const data: UserRolesCache = {
    roles,
    isAdmin: roles.includes('admin'),
    isContributor: roles.includes('contributor'),
    timestamp: Date.now(),
  };
  cache.roles.set(userId, data);
  persistToStorage();
}

/**
 * Get cached user profile (returns null if not cached or expired)
 */
export function getCachedProfile(userId: string): UserProfileCache | null {
  const cached = cache.profiles.get(userId);
  if (!cached) return null;
  
  if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
    cache.profiles.delete(userId);
    return null;
  }
  
  return cached;
}

/**
 * Set user profile in cache
 */
export function setCachedProfile(userId: string, profile: Omit<UserProfileCache, 'timestamp'>): void {
  const data: UserProfileCache = {
    ...profile,
    timestamp: Date.now(),
  };
  cache.profiles.set(userId, data);
  persistToStorage();
}

/**
 * Invalidate all cache for a user (call on profile update, logout, etc.)
 */
export function invalidateUserCache(userId: string): void {
  cache.roles.delete(userId);
  cache.profiles.delete(userId);
  persistToStorage();
}

/**
 * Clear all cached data (call on logout)
 */
export function clearAllUserCache(): void {
  cache.roles.clear();
  cache.profiles.clear();
  try {
    localStorage.removeItem(LS_ROLES_KEY);
    localStorage.removeItem(LS_PROFILE_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Get cache stats for debugging
 */
export function getUserCacheStats(): { rolesCount: number; profilesCount: number } {
  return {
    rolesCount: cache.roles.size,
    profilesCount: cache.profiles.size,
  };
}

